import { Grid, IconButton, Popper, Typography } from "@material-ui/core";
import { clsx } from "clsx";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useSelector } from "react-redux";
import useOnClickOutside from "../../learning-path/hooks/useOnClickOutside";
import {
  type AmplitudeEvent,
  type GamePlanEventProperties,
} from "../../models/amplitude";
import { ZoomableImage } from "../../shared/components";
import {
  DiamonIcon,
  NotesIcon,
  SuccessIcon,
} from "../../shared/icons/gamePlan";
import { type RootState } from "../../store";
import { HighlightForm } from "../highlight-form";
import { LessonCollectableForm } from "../lesson-collectable-form";
import { MentorTip } from "../mentor-tip";
import { type GamePlanSlide } from "../wizard/use-game-plan";
import { useStyles } from "./slider.styles";

type Toast = {
  message: string;
  isOpen: boolean;
  status: "success" | "error";
};

const ON_HIGHLIGHT_SUCCESS =
  "Your Highlight has been saved successfully. You can find i in your Financial Profile";
const ON_ERROR = "There was an error, please try again.";

const DEFAULT_TOAST_STATE: Toast = {
  isOpen: false,
  status: "success",
  message: "",
};

type Props = {
  amplitudeData?: AmplitudeEvent<GamePlanEventProperties>;
  slides: GamePlanSlide[];
  onBottomScroll?: () => void;
  showIcons?: boolean;
};

export const Slider = ({
  amplitudeData,
  slides,
  onBottomScroll,
  showIcons,
}: Props) => {
  const listInnerRef = useRef();
  const page = useRef(0);
  const [slide, setSlide] = useState(slides[page.current]);
  const classes = useStyles();

  const [toast, setToast] = useState(DEFAULT_TOAST_STATE);
  const [isHighlightsDialogOpen, setIsHighlightsDialogOpen] = useState(false);
  useState(false);
  const [HighlightsDialogAnchorEl, setHighlightsDialogAnchorEl] =
    useState<null | HTMLElement>(null);
  const highlightsDialogRef = useRef();

  const isPremium = useSelector<RootState, boolean>(
    (state) => state.profile.isPremium
  );

  useOnClickOutside(highlightsDialogRef, () =>
    setIsHighlightsDialogOpen(false)
  );

  const handleOpenAddHighlightsDialog = (event: MouseEvent<HTMLElement>) => {
    setHighlightsDialogAnchorEl(event.currentTarget);
    setIsHighlightsDialogOpen(true);
  };

  const handleCloseAddHighlightsDialog = () => {
    setIsHighlightsDialogOpen(false);
    setToast(DEFAULT_TOAST_STATE);
  };

  const canHighlightsDialogBeOpen =
    isHighlightsDialogOpen && Boolean(HighlightsDialogAnchorEl);
  const highlightsDialogId = canHighlightsDialogBeOpen
    ? "transition-popper"
    : undefined;

  const [highlightsDialogArrowRef, setHighlightsDialogArrowRef] =
    useState(null);

  const [isLessonCollectableDialogOpen, setIsLessonCollectableDialogOpen] =
    useState(false);
  const [
    isLessonCollectableSaveSuccessOpen,
    setIsLessonCollectableSaveSuccessOpen,
  ] = useState(false);
  const [lessonCollectableDialogAnchorEl, setLessonCollectableDialogAnchorEl] =
    useState<null | HTMLElement>(null);
  const lessonCollectableDialogRef = useRef();

  useOnClickOutside(lessonCollectableDialogRef, () =>
    setIsLessonCollectableDialogOpen(false)
  );

  const handleOpenAddLessonCollectableDialog = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setLessonCollectableDialogAnchorEl(event.currentTarget);
    setIsLessonCollectableDialogOpen((previousOpen) => !previousOpen);
  };

  const handleCloseAddLessonCollectableDialog = () => {
    setIsLessonCollectableDialogOpen((previousOpen) => !previousOpen);
    setIsLessonCollectableSaveSuccessOpen(false);
  };

  const handleSaveSuccessAddLessonCollectableDialog = () => {
    setIsLessonCollectableSaveSuccessOpen(true);
  };

  const canLessonCollectableDialogBeOpen =
    isLessonCollectableDialogOpen && Boolean(lessonCollectableDialogAnchorEl);
  const lessonCollectableDialogId = canLessonCollectableDialogBeOpen
    ? "transition-popper"
    : undefined;

  const [lessonCollectableDialogArrowRef, setLessonCollectableDialogArrowRef] =
    useState(null);

  const moveSliderPage = useCallback(
    (pageNum: number) => {
      const newPageNum = page.current + pageNum;
      if (newPageNum < 0 || newPageNum > slides.length - 1) return;

      if (newPageNum == slides.length - 1) onBottomScroll?.();

      page.current = newPageNum;
      setSlide(slides[newPageNum]);
    },
    [onBottomScroll, slides]
  );

  const sliderKeyEvent = useCallback(
    (e) => {
      if (e.keyCode == 38) moveSliderPage(-1);
      else if (e.keyCode == 40) moveSliderPage(1);
    },
    [moveSliderPage]
  );

  useEffect(() => {
    document.addEventListener("keydown", sliderKeyEvent, false);

    return () => {
      document.removeEventListener("keydown", sliderKeyEvent, false);
    };
  }, [sliderKeyEvent]);

  const onScroll = (event) => {
    if (
      listInnerRef.current &&
      !isHighlightsDialogOpen &&
      !isLessonCollectableDialogOpen
    ) {
      moveSliderPage(event.nativeEvent.wheelDelta > 0 ? -1 : 1);
    }
  };

  const renderSlide = (slide: GamePlanSlide) => {
    let imageComponent = <img src={slide?.imagePath} />;

    if (slides.length && page.current) {
      imageComponent = <ZoomableImage src={slide?.imagePath} />;
    }

    return (
      <Grid container className={classes.introDetails}>
        <Grid item lg={5} xs={5}>
          {imageComponent}
        </Grid>
        <Grid item lg={7} xs={7}>
          <Typography variant="h6" className={classes.intro}>
            {slide?.title}
          </Typography>
          <p className={classes.body}>{slide?.body}</p>
        </Grid>
      </Grid>
    );
  };

  const onHighlightCreate = (status) => {
    setIsHighlightsDialogOpen(false);
    return setToast({
      status,
      isOpen: true,
      message: status === "success" ? ON_HIGHLIGHT_SUCCESS : ON_ERROR,
    });
  };

  return (
    <>
      {toast.isOpen && (
        <Grid container>
          <Grid item xs={12}>
            <MentorTip
              variant={toast.status}
              title={
                <Typography>
                  {toast.status === "success" && <SuccessIcon />}
                  <b>{toast.message}</b>.
                </Typography>
              }
            />
          </Grid>
        </Grid>
      )}
      {isLessonCollectableSaveSuccessOpen && (
        <Grid container>
          <Grid item xs={12}>
            <MentorTip
              variant="success"
              title={
                <Typography>
                  <SuccessIcon />
                  <b>
                    Your Lesson Collectible Has Been Saved Successfully. You Can
                    Find It In Your Collectibles Tab On the Learning Page
                  </b>
                  .
                </Typography>
              }
            />
          </Grid>
        </Grid>
      )}
      <Grid container onWheel={onScroll} ref={listInnerRef}>
        <Grid item xs={11}>
          {renderSlide(slide)}
        </Grid>

        <Grid item xs={1}>
          {showIcons && (
            <>
              <Grid item xs={12} className={classes.alignCenter}>
                <IconButton
                  color="inherit"
                  onClick={handleOpenAddLessonCollectableDialog}
                  disabled={
                    isLessonCollectableDialogOpen || isHighlightsDialogOpen
                  }
                >
                  <DiamonIcon />
                </IconButton>
                <Popper
                  id={lessonCollectableDialogId}
                  open={isLessonCollectableDialogOpen}
                  anchorEl={lessonCollectableDialogAnchorEl}
                  placement="left-start"
                  transition
                  disablePortal
                  className={classes.popper}
                  modifiers={{
                    name: "arrow",
                    enabled: true,
                    options: {
                      element: lessonCollectableDialogArrowRef,
                    },
                  }}
                >
                  <div ref={lessonCollectableDialogRef}>
                    <span
                      className={classes.arrow}
                      ref={setLessonCollectableDialogArrowRef}
                    />
                    <LessonCollectableForm
                      slide={slide}
                      onClose={handleCloseAddLessonCollectableDialog}
                      onLessonCollectableSaveSuccess={
                        handleSaveSuccessAddLessonCollectableDialog
                      }
                    />
                  </div>
                </Popper>
              </Grid>
              <Grid item xs={12} className={classes.alignCenter}>
                {isPremium && (
                  <IconButton
                    color="inherit"
                    onClick={handleOpenAddHighlightsDialog}
                    disabled={
                      isLessonCollectableDialogOpen || isHighlightsDialogOpen
                    }
                  >
                    <NotesIcon />
                  </IconButton>
                )}
                <Popper
                  id={highlightsDialogId}
                  open={isHighlightsDialogOpen}
                  anchorEl={HighlightsDialogAnchorEl}
                  placement="left-start"
                  transition
                  disablePortal
                  className={classes.popper}
                  modifiers={{
                    name: "arrow",
                    enabled: true,
                    options: {
                      element: highlightsDialogArrowRef,
                    },
                  }}
                >
                  <div ref={highlightsDialogRef}>
                    <span
                      className={classes.arrow}
                      ref={setHighlightsDialogArrowRef}
                    />
                    <HighlightForm
                      slide={slide}
                      amplitudeData={amplitudeData}
                      onClose={handleCloseAddHighlightsDialog}
                      onHighlightSaveSuccess={() =>
                        onHighlightCreate("success")
                      }
                      onHighlightSaveError={() => onHighlightCreate("error")}
                    />
                  </div>
                </Popper>
              </Grid>
            </>
          )}
          <div
            id="discover-slider-wt"
            className={classes.slideIndicatorContainer}
          >
            {slides.map((s) => (
              <Grid key={s.id} item xs={12}>
                <hr
                  className={clsx(classes.slideIndicator, {
                    [classes.slideActiveIndicator]: s.id === slide.id,
                  })}
                />
              </Grid>
            ))}
          </div>
        </Grid>
      </Grid>
    </>
  );
};
